import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import Typewriter from "typewriter-effect";
import { app, storage } from "../config/firebaseConfig";
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { httpsCallable, getFunctions } from "firebase/functions";
import script from "../script_denuncias_novo.json";
import micIcon from '../assets/mic_icon.png';
import sendIcon from "../assets/send_icon.png";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useParams, Link } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import { fetchSignedVideos } from '../utils/videoApi';
import { requestFullscreen } from '../utils/fullscreen';
import { CORES, TIPOGRAFIA, FORMA } from '../utils/layoutOficial';

const videoSequence = [
  "intro", "p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8", "p9", "p10", "p11", "p12", "p13", "p14", "fim"
];

export default function EIADenuncia() {
  const { company } = useParams();
  const [isValidCompany, setIsValidCompany] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [videoUrls, setVideoUrls] = useState({});
  const videoRef = useRef(null);
  const messageEndRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimer = useRef(null);
  const [videoSpeed, setVideoSpeed] = useState(1.25);
  const [protocoloID, setProtocoloID] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('');
  const [stepIdentificacao, setStepIdentificacao] = useState(null);
  const [mostrarOpcoesIdentificacao, setMostrarOpcoesIdentificacao] = useState(false);
  const [inputBloqueado, setInputBloqueado] = useState(false);
  const identificacaoVideos = ["p1.1", "p1.2", "p1.3", "p1.4", "p1.5"];
  const [isIdentificacaoAtiva, setIsIdentificacaoAtiva] = useState(false);
  const [identificacaoStepIndex, setIdentificacaoStepIndex] = useState(0);
  const [mensagemAuxiliar, setMensagemAuxiliar] = useState(null);
  const [opcoesResumo, setOpcoesResumo] = useState(false);
  const [editedResumo, setEditedResumo] = useState('');
  const [isEditingResumo, setIsEditingResumo] = useState(false);
  const inputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const dadosGPTRef = useRef(null);
  const editedResumoRef = useRef(null);
  const [ultimoTranscript, setUltimoTranscript] = useState("");
  const [resumoGeradoIA, setResumoGeradoIA] = useState("");

  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const functions = useMemo(() => getFunctions(app, "southamerica-east1"), []);

  const questions = script.denuncia.flatMap(section => section.perguntas || []);
  const identificacaoPerguntas = questions.filter(q => q.condicional === "Me identificar");

  useEffect(() => {
    let isMounted = true;
    const loadVideos = async () => {
      const videos = await fetchSignedVideos('eia/media/denuncia');
      if (!isMounted) return;
      setVideoUrls(videos);
    };
    loadVideos();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Antes lia direto company/{company} via SDK do cliente — esse caminho é
    // bloqueado de propósito em firestore.rules (Hardening LEGACY-001, "allow
    // read: if false"), então batia em permission-denied sem cair em nenhum
    // catch, deixando a tela travada pra sempre em "Verificando empresa..."
    // pra qualquer empresa. Agora passa por uma Cloud Function (mesmo padrão
    // de getCompanyPublicLabel/getCompanyLogoUrl), checando cliente/{company}
    // (o caminho real). Em caso de falha da própria function, trata como
    // "empresa não encontrada" em vez de deixar a tela presa pra sempre.
    let cancelled = false;
    const checkCompanyExists = async () => {
      try {
        const callable = httpsCallable(functions, "checkCompanyExists");
        const { data } = await callable({ company });
        if (!cancelled) setIsValidCompany(!!data?.exists);
      } catch (error) {
        console.error("Erro ao checar existência da empresa:", error);
        if (!cancelled) setIsValidCompany(false);
      }
    };

    checkCompanyExists();
    return () => {
      cancelled = true;
    };
  }, [company, functions]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
    }
  }, [userInput]);

  const handleIdentificacao = (escolha) => {
    setMessages((prev) => [...prev, { type: "user", text: escolha === "anonimo" ? "Permanecer anônimo" : "Me identificar" }]);
    setMostrarOpcoesIdentificacao(false);
    setInputBloqueado(false);
    setWaitingForResponse(false);
  
    if (escolha === "identificado") {
      setMessages((prev) => [...prev, { type: "ia", text: identificacaoPerguntas[0].texto }]);
      setIsIdentificacaoAtiva(true);
      setIdentificacaoStepIndex(0);
      setStepIdentificacao(0);
      setIsPlaying(true);
    } else {
      const pergunta2Index = questions.findIndex(q => q.id === 2);
      setCurrentQuestionIndex(pergunta2Index);
      setMessages((prev) => [...prev, { type: "ia", text: questions[pergunta2Index]?.texto }]);
      setCurrentVideoIndex(videoSequence.indexOf("p2"));
    }
  
    setIsPlaying(true);
  };

  const handleOpcoes = async (escolha) => {
    if (escolha === "Enviar") {
      setResumoGeradoIA(editedResumo); 
      setOpcoesResumo(false);
      setInputBloqueado(false);
      setIsEditingResumo(false);
      setMessages((prev) => [...prev, { type: "user", text: editedResumo }]);
      await handleSendMessage(null, "Enviar");
    }
  
    if (escolha === "Editar") {
      setOpcoesResumo(false);
      setIsEditingResumo(true); 
    }
  };

  const formatTime = (seconds) => {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
        if (messageEndRef.current) {
          messageEndRef.current.scrollTop = messageEndRef.current.scrollHeight;
        }
    }, 100);

    return () => clearInterval(interval);
  }, [messages]);

  const toggleTranscription = () => {
      if (isListening) {
          SpeechRecognition.stopListening();
          clearInterval(recordingTimer.current);
          setIsTranscribing(false);
      } else {
          SpeechRecognition.startListening({ continuous: true, language: 'pt-BR' });
          setRecordingTime(0);
          recordingTimer.current = setInterval(() => {
              setRecordingTime(prevTime => prevTime + 1); // Incrementa o tempo
          }, 1000);
      }
      setIsListening(!isListening);
      resetTranscript();
      setUltimoTranscript("");
  };

  useEffect(() => {
    if (listening) {
      let texto = transcript;
      
      const ultimaPerguntaIA = [...messages]
      .reverse()
      .find(msg => msg.type === 'ia')?.text || "";

      if (ultimaPerguntaIA.includes("e-mail")) {
        texto = texto
          .normalize("NFD")                      // separa acentos das letras
          .replace(/[\u0300-\u036f]/g, "")       // remove acentos
          .replace(/\s+/g, "")                   // remove espaços
          .toLowerCase()                         // tudo minúsculo
          .replace(/[^a-z0-9@._-]/g, "");        // remove tudo que não for permitido
      }

      if (ultimaPerguntaIA.includes("CPF")) {
        texto = texto.replace(/\D/g, ""); // remove não dígitos
        if (texto.length === 11) {
          texto = texto.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
        }
      }    
      
      setUserInput(texto);
      setIsTranscribing(true);
    }
  }, [transcript, listening, messages, isEditingResumo, ultimoTranscript]);

  const handleStart = () => {
    requestFullscreen();
    setIsStarted(true);
    setMessages([{ type: "ia", text: script.denuncia[0].intro }]);
    setIsPlaying(true);
  };

  useEffect(() => {
      const interval = setInterval(() => {
          if (videoRef.current) {
              videoRef.current.playbackRate = videoSpeed;
          }
      }, 1000); 

      return () => clearInterval(interval); 
  }, [videoSpeed]);

  const handleVideoEnd = async () => {
    if (isIdentificacaoAtiva) {
      setWaitingForResponse(true);
      return;
    }

    if (currentVideoIndex === videoSequence.length - 1) {
      setWaitingForResponse(true);
    } else {
      setIsPlaying(false);
      setWaitingForResponse(true);
    }
  };

  const handleFileSelection = (event) => {
    const filesArray = Array.from(event.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...filesArray]);
  };
  
  const removeFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };
  
  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
    setModalType('');
  };

  const uploadSelectedFiles = async () => {
    let finalProtocoloID = protocoloID;

    // Só gera se ainda não existir. É só um candidato local (sem tocar o
    // Firestore) pra ter um número estável pro caminho de upload de
    // evidências antes do envio final — a checagem de unicidade de verdade
    // acontece no servidor, em submitDenunciaFormulario (Fase 0, ver
    // Denuncia.js/saveToDatabase abaixo e denunciaFormulario.js).
    if (!finalProtocoloID) {
      finalProtocoloID = generateCandidateProtocolo();
      setProtocoloID(finalProtocoloID);
    }

    if (!finalProtocoloID) {
      setModalMessage('Protocolo ainda não gerado.');
      setModalType('error');
      setShowModal(true);
      setTimeout(closeModal, 3000);
      return;
    }
  
    const storagePath = `eia/denuncias/${company}/${finalProtocoloID}`;
  
    if (selectedFiles.length === 0) {
      setModalMessage('Nenhum arquivo selecionado para upload.');
      setModalType('error');
      setShowModal(true);
      setTimeout(closeModal, 3000);
      return;
    }
  
    const uploadPromises = selectedFiles.map(async (file) => {
      const fileRef = storageRef(storage, `${storagePath}/${file.name}`);
      await uploadBytes(fileRef, file);
    });
  
    try {
      await Promise.all(uploadPromises);
      setModalMessage('Arquivos enviados com sucesso!');
      setModalType('success');
      setSelectedFiles([]);
    } catch (error) {
      console.error("Erro ao enviar arquivos:", error);
      setModalMessage('Erro ao enviar arquivos.');
      setModalType('error');
    }
  
    setShowModal(true);
    setTimeout(closeModal, 3000);
  };  

  // Fase 0 (2026-09-01): antes, isto lia o Firestore (getDoc) pra checar
  // unicidade contra `eia/denuncia/entrevistas/{id}` — um caminho que
  // firestore.rules já nega leitura hoje (cai no catch-all deny-all, não há
  // nenhum match pra `eia/**`), então essa checagem já falhava com
  // permission-denied antes mesmo de chegar no salvamento. Virou um gerador
  // puramente local (sem tocar o Firestore): só serve pra ter um número
  // estável pro caminho de upload de evidências antes do envio final — a
  // checagem de unicidade de verdade agora é feita no servidor, via
  // submitDenunciaFormulario (Admin SDK, ver saveToDatabase abaixo).
  const generateCandidateProtocolo = () => Math.floor(10000000 + Math.random() * 90000000).toString();

  const handleSendMessage = async (e, forcedMessage = null) => {
    if (e?.preventDefault) e.preventDefault();

    const message = forcedMessage || userInput.trim();
    if (message !== "") {
      setMessages((prev) => [...prev, { type: "user", text: message }]);
      const trimmedInput = userInput.trim().toLowerCase();
      setUserInput("");

      if (isListening) {
        SpeechRecognition.stopListening();
        clearInterval(recordingTimer.current);
        setIsListening(false);
        setIsTranscribing(false);
        setRecordingTime(0);
        resetTranscript();
      }
      
      setWaitingForResponse(false);

      if (stepIdentificacao !== null) {
        const nextStep = stepIdentificacao + 1;
        if (nextStep < identificacaoPerguntas.length) {
          setStepIdentificacao(nextStep);
          setIdentificacaoStepIndex(nextStep); 
          setMessages((prev) => [...prev, { type: "ia", text: identificacaoPerguntas[nextStep].texto }]);
        } else {
          setStepIdentificacao(null);
          setIsIdentificacaoAtiva(false);
          const proximaPerguntaIndex = questions.findIndex(q => q.id === 2);
          setCurrentQuestionIndex(proximaPerguntaIndex);
          setMessages((prev) => [...prev, { type: "ia", text: questions[proximaPerguntaIndex].texto }]);
          setCurrentVideoIndex(videoSequence.indexOf("p2"));
        }
        setIsPlaying(true);
        return;
      }

      if (currentVideoIndex === videoSequence.length - 2){
          if (trimmedInput.toLowerCase().includes("sim")) {
            setMensagemAuxiliar("");
            const candidateProtocoloID = protocoloID || generateCandidateProtocolo();

            const finalProtocoloID = await saveToDatabase(candidateProtocoloID);

            setMessages((prev) => [
              ...prev,
              { type: "ia", text: script.denuncia[1].fim.replace("[GERAR NÚMERO]", finalProtocoloID || candidateProtocoloID) },
            ]);
            setCurrentVideoIndex((prev) => prev + 1);
            setIsPlaying(true);
            return;
          } else {
          const autorizeUrl = videoUrls['autorize'];
          if (!autorizeUrl) return;
          const video = document.createElement("video");
          video.src = autorizeUrl;
          videoRef.current.src = video.src;
          videoRef.current.play();
          setIsPlaying(true);
            setMensagemAuxiliar("Por favor, autorize digitando 'sim' para podermos continuar.");
            return;
          }
      } 

      const proximoIndex = currentQuestionIndex + 1;

      if (proximoIndex === 1) {
        const textoPergunta1 = questions.find(q => q.id === 1)?.texto;
        setMessages((prev) => [...prev, { type: "ia", text: textoPergunta1 }]);
        setCurrentVideoIndex(videoSequence.indexOf("p1"));
        setIsPlaying(true);
        
        setTimeout(() => {
          setMostrarOpcoesIdentificacao(true);
          setInputBloqueado(true);
          setCurrentQuestionIndex(proximoIndex);
        }, 500);
        
        return;
      }

      const nextQuestion = questions[proximoIndex]?.texto;

      if (questions[proximoIndex]?.id === "13.1") {
        setInputBloqueado(true);

        const getResposta = (idTexto) => {
          const perguntaIndex = messages.findIndex(
            (msg) => msg.type === "ia" && msg.text.includes(idTexto)
          );
          return perguntaIndex !== -1 ? messages[perguntaIndex + 1]?.text || "" : "";
        };
      
        const nomeDenunciante = getResposta("Qual é o seu nome completo?") || "";
        const tipoDenuncia = getResposta("A que se refere a sua denúncia?") || "";
        const denunciados = getResposta("Você sabe o nome completo da pessoa") || "";
        const relato = getResposta("Agora, por favor, detalhe como o fato está ocorrendo.") || "";
        const tipoEvidencia = getResposta("você sabe informar se existem evidências") || "";
        const quemSabe = getResposta("Outras pessoas também têm conhecimento deste fato?") || "";
        const areaDenunciada = getResposta("Em qual empresa está ocorrendo o fato? Se você souber, pode especificar o setor ou a área envolvida?") || "";
        const liderancaSabe = getResposta("Alguma liderança da empresa tem ciência deste fato?") || "";
        const sugestao = getResposta("Você tem alguma sugestão sobre como resolver este problema?") || "";

        console.log("Chamando função com:", {
          nomeDenunciante,
          tipoDenuncia,
          denunciados,
          relato,
          tipoEvidencia,
          quemSabe,
          liderancaSabe,
          sugestao,
        });
      
        let resumoGerado = relato;
        setIsLoading(true);
        try {
          const generateResumo = httpsCallable(functions, "generateResumoReclamacao");
          const resumoResult = await generateResumo({
            nomeDenunciante,
            tipoDenuncia,
            denunciados,
            relato,
            tipoEvidencia,
            quemSabe,
            liderancaSabe,
            sugestao,
            areaDenunciada
          });

          console.log(resumoResult, 'resumoresult')
          console.log(resumoResult.data, 'resumeresult data')

          resumoGerado = resumoResult.data.resumo;

          const dadosSeparados = {
            nomeDenunciante: resumoResult.data.nomeDenunciante,
            tipoDenuncia: resumoResult.data.tipoDenuncia,
            denunciados: resumoResult.data.denunciados,
            relato: resumoResult.data.relato,
            quemSabe: resumoResult.data.quemSabe,
            liderancaSabe: resumoResult.data.liderancaSabe,
            sugestao: resumoResult.data.sugestao,
            areaDenunciada: resumoResult.data.areaDenunciada
          };

          dadosGPTRef.current = dadosSeparados;
        } catch (err) {
          console.warn("⚠️ Erro ao gerar resumo via GPT. Usando texto da pergunta 10:", err);
        } finally {
          setIsLoading(false);
        }

        setOpcoesResumo(true);
        const resumoVideo = videoUrls['resumo'];
        if (videoRef.current && resumoVideo) {
          videoRef.current.src = resumoVideo;
          videoRef.current.play();
        }

        setTimeout(() => {
          setMessages((prev) => [...prev, { type: "ia", text: resumoGerado }]);
          setEditedResumo(resumoGerado);
          setIsPlaying(true);
          setCurrentQuestionIndex(proximoIndex);
        }, 500);
      
        return; 
      }

      if (nextQuestion) {
        setTimeout(() => {
          setMessages((prev) => [...prev, { type: "ia", text: nextQuestion }]);
          setIsPlaying(true);
          if (currentVideoIndex < videoSequence.length - 1) {
            setCurrentVideoIndex((prev) => prev + 1);
          }
          setCurrentQuestionIndex(proximoIndex);
        }, 500);
      }
    }
  };

  const handleSpeedChange = (newSpeed) => {
      setVideoSpeed(newSpeed);
      if (videoRef.current) {
          videoRef.current.playbackRate = newSpeed;
      }
  };

  const saveToDatabase = async (protocoloID) => {
    const formattedMessages = messages
      .map((message, index) => {
        if (message.type === "ia") {
          const questionText = message.text;
          const answerMessage = messages[index + 1];
          let answerText = answerMessage?.type === "user" ? answerMessage.text : null; 

          return {
            question: questionText,
            answer: answerText,
          };
        }
        return null;
      })
      .filter(Boolean);

      const {
        nomeDenunciante = "",
        quemSabe = "",
        liderancaSabe = "",
        sugestao = "",
        tipoDenuncia = "",
        denunciados = "",
        areaDenunciada = "",
      } = dadosGPTRef.current || {};

      setIsLoading(true);
    // Fase 0 (2026-09-01): antes gravava direto no Firestore via `setDoc`
    // client-side em `eia/denuncias/{company}/{protocoloID}` — caminho sem
    // NENHUMA regra explícita em firestore.rules, então caía no catch-all
    // deny-all (permission-denied real em produção, denúncia anônima real
    // não conseguia ser salva). Agora passa por submitDenunciaFormulario
    // (Cloud Function pública, Admin SDK, sem req.auth) — mesmo padrão que
    // sendEntrevistaContinuaTurn/createAprofundamentoSession já usam pra
    // escrita server-side de fluxos públicos/anônimos. `protocoloID` aqui é
    // só um CANDIDATO local (ver generateCandidateProtocolo); o servidor
    // confirma (ou troca, em caso raríssimo de colisão) o número final.
    try {
      const submitDenunciaFormulario = httpsCallable(functions, "submitDenunciaFormulario");
      const { data } = await submitDenunciaFormulario({
        company,
        candidateProtocoloID: protocoloID,
        responses: formattedMessages,
        nomeDenunciante,
        quemSabe,
        liderancaSabe,
        sugestao,
        tipoDenuncia,
        denunciados,
        areaDenunciada,
        resumoGeradoporIa: resumoGeradoIA,
      });
      const confirmedProtocoloID = data?.protocoloID || protocoloID;
      setProtocoloID(confirmedProtocoloID);
      setShowConfirmationModal(true);
      return confirmedProtocoloID;
    } catch (error) {
      console.error("Erro ao salvar a denúncia: ", error);
      setModalMessage('Não foi possível enviar sua denúncia. Tente novamente em instantes.');
      setModalType('error');
      setShowModal(true);
      setTimeout(closeModal, 4000);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidCompany === null) return <Wrapper>Verificando empresa...</Wrapper>;

  if (!isValidCompany)
    return (
      <Wrapper>
        <Container>
          <h1>A empresa "{company}" não possui um canal de denúncias na nossa plataforma.</h1>
        </Container>
      </Wrapper>
    );

  return (
    <Wrapper>
      {!isStarted ? (
        <Container>
          <Marca>
            <span className="sigla" aria-hidden="true">{(company || '?').charAt(0).toUpperCase()}</span>
            <span className="nome">{company.toUpperCase()}</span>
            <span className="operado">Canal de denúncias · operado pela DUE</span>
          </Marca>
          <h1>Bem-vindo ao EIA Denúncias da empresa {company.toUpperCase()}.</h1>
          <Button onClick={handleStart}><p>Iniciar</p></Button>
          {/* MD1 (Fase 1): canal alternativo por avatar, sem substituir este
              formulário — a pessoa escolhe qual prefere usar. */}
          <AlternativeChannelLink to={`/${company}/denuncia-com-avatar`}>
            Prefere conversar com um atendente virtual?
          </AlternativeChannelLink>
        </Container>
      ) : (
        <MainContainer>
          <PageContainer>
              <SpeedSelectorContainer>
                <p>Velocidade:</p>
                <SpeedButton
                    onClick={() => handleSpeedChange(1)}
                    selected={videoSpeed === 1}
                >
                    1x
                </SpeedButton>
                <SpeedButton
                    onClick={() => handleSpeedChange(1.25)}
                    selected={videoSpeed === 1.25}
                >
                    1.25x
                </SpeedButton>
                <SpeedButton
                    onClick={() => handleSpeedChange(1.5)}
                    selected={videoSpeed === 1.5}
                >
                    1.5x
                </SpeedButton>
                <SpeedButton
                    onClick={() => handleSpeedChange(2)}
                    selected={videoSpeed === 2}
                >
                    2x
                </SpeedButton>
            </SpeedSelectorContainer>
            <video
              ref={videoRef}
              preload="auto"
              autoPlay
              onEnded={handleVideoEnd}
              type="video/mp4"
              src={
                isIdentificacaoAtiva
                  ? videoUrls[identificacaoVideos[identificacaoStepIndex]]
                  : videoUrls[videoSequence[currentVideoIndex]]
              }
            />
            <MessageContainer ref={messageEndRef}>
              {messages.map((msg, index) => (
                <MessageBox key={index} type={msg.type}>
                  <Message>
                    {msg.type === "ia" ? (
                      <Typewriter
                        onInit={(typewriter) => {
                          typewriter.typeString(msg.text).start();
                        }}
                        options={{
                          autoStart: true,
                          loop: false,
                          delay: 35,
                          cursor: "",
                        }}
                      />
                    ) : (
                      msg.text
                    )}
                  </Message>
                </MessageBox>
              ))}

            {mensagemAuxiliar && (
                <MessageBox type="ia">
                  <Message>{mensagemAuxiliar}</Message>
                </MessageBox>
              )}

              {currentQuestionIndex === 1 && mostrarOpcoesIdentificacao && (
                <OptionButtonsContainer>
                  <OptionButton onClick={() => handleIdentificacao("anonimo")}>Permanecer anônimo</OptionButton>
                  <OptionButton onClick={() => handleIdentificacao("identificado")}>Me identificar</OptionButton>
                </OptionButtonsContainer>
              )}
              {isEditingResumo ? (
                <BottomEditorContainer>
                  <form onSubmit={() => handleOpcoes("Enviar")} style={{ display: 'flex', alignItems: 'center' }}>
                    <StyledInput
                       ref={editedResumoRef}
                      value={editedResumo}
                      onChange={(e) => setEditedResumo(e.target.value)}
                      placeholder="Edite seu resumo aqui..."
                      disabled={false}
                    />
                    <SendButton type="submit" disabled={false}>
                      <img src={sendIcon} alt="send" />
                    </SendButton>
                  </form>
                </BottomEditorContainer>
              ) : opcoesResumo && (
                <OptionButtonsContainer>
                  <OptionButton onClick={() => handleOpcoes("Editar")}>Complementar resposta</OptionButton>
                  <OptionButton onClick={() => handleOpcoes("Enviar")}>Enviar</OptionButton>
                </OptionButtonsContainer>
              )}
            </MessageContainer>
            {!isEditingResumo && (
              <InteractiveContainer>
                <form onSubmit={handleSendMessage}>
                  <StyledInput
                    ref={inputRef}
                    type="text"
                    placeholder={isPlaying ? "Aguarde..." : "Envie sua resposta"}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    isTranscribing={isTranscribing}
                    disabled={inputBloqueado}
                  />
                  <SendButton type="submit" disabled={!waitingForResponse || inputBloqueado}>
                    <img src={sendIcon} alt="send"  />
                  </SendButton>
                </form>
                <MicTimer isListening={isListening}>
                    <MicButton onClick={toggleTranscription} isListening={isListening}>
                        <img src={micIcon} alt="microphone" />
                    </MicButton>
                    {isListening && (
                        <TimerContainer>
                            {formatTime(recordingTime)}
                        </TimerContainer>
                    )}
                </MicTimer>
              </InteractiveContainer>
            )}
          </PageContainer>
          {currentQuestionIndex === 17 && ( 
            <EvidenceContainer>
              <label htmlFor="file-upload">Selecionar Evidências</label>
              <input id="file-upload" type="file" multiple onChange={handleFileSelection} />
              <div>
                {selectedFiles.map((file, index) => (
                  <SelectedFile key={index}>
                    {file.name}
                    <RemoveButton onClick={() => removeFile(index)}>x</RemoveButton>
                  </SelectedFile>
                ))}
              </div>
              {selectedFiles.length > 0 && (
                <button type="button" onClick={uploadSelectedFiles}>Enviar</button>
              )}
            </EvidenceContainer>
          )}
        </MainContainer>
      )}
      {showConfirmationModal && (
        <Modal>
          <ModalContent>
            <p>Denúncia enviada com sucesso! Obrigado por colaborar. Seu número de protocolo: {protocoloID}</p>
            <button onClick={() => setShowConfirmationModal(false)}>Fechar</button>
          </ModalContent>
        </Modal>
      )}
      {showModal && (
          <Modal>
            <ModalContent>
              <p style={{ color: modalType === 'success' ? 'green' : 'red' }}>
                {modalMessage}
              </p>
            </ModalContent>
          </Modal>
        )}
        {isLoading && (
          <Modal>
            <ModalContent>
              <Oval height={60} width={60} color="#000" secondaryColor="#ccc" strokeWidth={4} />
              <p style={{ marginTop: 20 }}>Processando sua resposta, por favor aguarde...</p>
            </ModalContent>
          </Modal>
        )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 20px;
  background-color: ${CORES.papel};
`;

/**
 * A abertura do canal, no LAYOUT OFICIAL (ver utils/layoutOficial.js).
 *
 * Antes de 04/10/2026 esta tela era um título gigante e um botão preto sobre
 * cinza, sem nada que a ligasse ao produto: nem a identificação da empresa, nem
 * quem opera o canal. Quem chega aqui por um link não sabe onde está — e é uma
 * tela que recebe denúncia, onde saber onde se está é parte de confiar.
 */
const Container = styled.div`
  width: 100%;
  max-width: 720px;
  background-color: ${CORES.superficie};
  border: 1px solid ${CORES.borda};
  border-radius: ${FORMA.raioCartao};
  padding: 44px 40px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: ${CORES.tinta};

  h1 {
    margin: 18px 0 0;
    font-family: ${TIPOGRAFIA.titulo};
    font-size: 30px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -.02em;
    color: ${CORES.tinta};
  }

  @media only screen and (max-width: 600px) {
    padding: 32px 22px 28px;
    h1 { font-size: 24px; }
  }
`;

/** Quem é a empresa, e quem opera o canal. Dado, não enfeite. */
const Marca = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  padding-bottom: 18px;
  border-bottom: 1px solid ${CORES.borda};
  font-family: ${TIPOGRAFIA.corpo};
  text-align: left;

  .sigla {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: ${FORMA.raioBotao};
    background: ${CORES.tinta};
    color: ${CORES.superficie};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
  }
  .nome { font-weight: 700; font-size: 16px; color: ${CORES.tinta}; }
  .operado { margin-left: auto; font-size: 12px; color: ${CORES.apagado}; }

  @media only screen and (max-width: 600px) {
    .operado { display: none; }
  }
`;

const AlternativeChannelLink = styled(Link)`
    margin-top: 18px;
    color: ${CORES.acao};
    font-family: ${TIPOGRAFIA.corpo};
    font-size: 15px;
    text-decoration: none;
    cursor: pointer;

    &:hover { text-decoration: underline; }
`;

const Button = styled.button`
    margin-top: 28px;
    min-width: 260px;
    height: 50px;
    padding: 0 32px;
    background-color: ${CORES.acao};
    border: none;
    border-radius: ${FORMA.raioBotao};
    cursor: pointer;
    p {
        margin: 0;
        color: #fff;
        font-family: ${TIPOGRAFIA.corpo};
        font-size: 17px;
        font-weight: 600;
    }
    &:hover { filter: brightness(1.12); }
    &:focus-visible { outline: 2px solid ${CORES.acao}; outline-offset: 3px; }
`;

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    @media only screen and (max-width: 480px) {
        width: 90%;
        height: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
`;

const PageContainer = styled.div`
  width: 500px;
  height: 720px;
  background-color: #FCFEFF;
  box-shadow: 20px 20px 20px #BACCDE8A;
  border: 1px solid #D1D1D1;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  position: relative;
  video {
    position: absolute;
    width: 1300px;
    border-radius: 16px;
    object-fit: cover;
  }

  @media only screen and (max-width: 480px) {
    width: 100%;
    min-width: 320px;
    padding: 0;
    border-radius: 10px;
    box-shadow: none;
  }
`

const StyledInput = styled.input.attrs(props => ({
  'data-islistening': props.isTranscribing
}))`
  width: 310px;
  height: 52px;
  z-index: 2;
  border-radius: 16px;
  border: none;
  background-color: rgba(0, 0, 0, 0.5);
  color: #FFF;
  padding-left: 16px;
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  text-align: left; 
  &:focus {
      outline: 1px solid #000;
  }

  @media only screen and (max-width: 480px) {
      width: 200px;
      height: 40px;
      font-size: 16px;
      padding-left: 10px;
  }
`;

const MessageContainer = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px; 
  margin-bottom: 90px;
  max-height: 260px;
  overflow-y: auto; 
  width: 90%;
  z-index: 4;
  &::-webkit-scrollbar {
      width: 0;
      display: none; 
  }
`;

const MessageBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${props => props.type === 'ia' ? 'flex-start' : 'flex-end'};
`

const Message = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  height: auto;
  padding: 15px; 
  border-radius: 12px; 
  width: fit-content;
`;

const InteractiveContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 22px;
  bottom: 20px;

  form {
      display: flex;
      align-items: center;
  }
`
const MicButton = styled.button`
  height: 52px;
  width: 52px;
  border-radius: 16px;
  border: none;
  background-color: ${props => props.isListening ? 'green' : 'rgba(0, 0, 0, 0.5)'};
  margin-left: 20px;
  cursor: pointer;

  @media only screen and (max-width: 480px) {
      width: 40px;
      height: 40px;
      margin-left: 5px;

      img {
          width: 24px;
          height: 24px;
      }
  }
`

const SendButton = styled.button`
    height: 52px;
    width: 52px;
    border-radius: 16px;
    border: none;
    background-color:  ${({ isListening }) => isListening ? 'red' : 'rgba(0, 0, 0, 0.5)'};
    margin-left: 10px;
    cursor: ${({ isListening }) => isListening ? 'not-allowed' : 'pointer'};
    pointer-events: ${({ isListening }) => isListening ? 'none' : 'auto'};

    @media only screen and (max-width: 480px) {
        width: 40px;
        height: 40px;
        margin-left: 5px;

        img {
        width: 24px;
        height: 24px;
        }
    }
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  font-family: "Poppins", sans-serif;
  font-size: 18px;

  button {
    margin-top: 20px;
    padding: 10px 20px;
    border: none;
    background: #000;
    color: #fff;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #333;
    }
  }
`;

const MicTimer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: ${({ isListening }) => (isListening ? '20px' : '0')};
    transition: margin-top 0.3s ease;
`

const TimerContainer = styled.div`
    font-family: 'Roboto', sans-serif;
    width: 40px;
    font-size: 14px;
    margin-left: 20px;
    color: #fff;
    text-align: center;
    margin-top: 5px;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    padding: 2px 2px;
`;

const SpeedSelectorContainer = styled.div`
    font-family: 'Roboto', sans-serif;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 5px 10px;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 12px;
    margin-bottom: 10px;
    position: absolute;
    top: 10px;
    left: 120px;
    transform: translateX(-50%);
    z-index: 10;
    
    p {
        color: white;
        font-size: 12px;
        font-weight: 500;
        margin: 0;
    }
`;

const SpeedButton = styled.button`
    background-color: ${(props) => (props.selected ? "#28a745" : "rgba(255, 255, 255, 0.2)")};
    color: white;
    border: none;
    padding: 4px 6px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 10px;
    transition: background-color 0.3s ease, transform 0.2s;

    &:hover {
        background-color: #218838;
        transform: scale(1.1);
    }
`;

const EvidenceContainer = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

  input[type="file"] {
    display: none;
  }

  label {
    display: inline-block;
    padding: 10px 20px;
    background-color: #28a745;
    color: white;
    font-size: 16px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    font-family: 'Roboto', sans-serif;

    &:hover {
      background-color: #218838;
    }
  }

  button {
    padding: 10px 20px;
    background-color: #28a745;
    color: white;
    font-size: 16px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    font-family: 'Roboto', sans-serif;

    &:hover {
      background-color: #218838;
    }
  }
`

const SelectedFile = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 10px;
    border-radius: 5px;
    margin-bottom: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    width: 100%;
`;

const RemoveButton = styled.button`
    background-color: transparent;
    border: none;
    color: red;
    cursor: pointer;
    font-size: 16px;
    margin-left: 10px;

    &:hover {
        color: darkred;
    }
`;

const OptionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const OptionButton = styled.button`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 16px;
  padding: 12px 20px;
  font-size: 16px;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #000;
  }

  @media only screen and (max-width: 480px) {
    font-size: 14px;
    padding: 10px 16px;
  }
`;

const BottomEditorContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 22px;
  bottom: 20px;
  z-index: 10;
  gap: 10px;

  form {
    display: flex;
    align-items: center;
  }
`;
